/**
 * The connector library's one organization filter (item 83, Segev 2026-11-02),
 * as pure functions (no React, no i18n). A scope is a list of PICKS: an
 * organization alone, or an organization and everything under it (`below`).
 * Three ways to choose, all on the same tree:
 *  - a click on a name: only that organization (`only`);
 *  - a box: several (`toggle`);
 *  - "+ all under it": that organization's whole branch (`toggleBelow`).
 * An organization inside a picked branch is COVERED: it is in the scope
 * whatever its own box says, and the tree shows it so.
 *
 * The tree is the Organizations tab's (utils/orgTree.ts).
 * `tests/unit/connector_library/scope.test.mjs` runs the real code.
 */

import { buildTree, flatten, type TreeNode, type TreeOrg } from './orgTree';

export interface Pick { id: string; below: boolean }

/** The most picks one choice may hold — the server's MAX_SCOPE_PICKS (a branch covers any number). */
export const MAX_PICKS = 50;

/** Every organization's node, in the tree's order (parents first, siblings by name). */
function nodes<T extends TreeOrg>(orgs: T[]): Map<string, TreeNode<T>> {
  return new Map(flatten(buildTree(orgs)).map(n => [n.org.tenant_id, n]));
}

/** `id` and every organization under it. */
function branch(byId: Map<string, TreeNode>, id: string): string[] {
  return byId.get(id)?.ids ?? [id];
}

/** Every organization the scope holds. */
export function scopeIds<T extends TreeOrg>(orgs: T[], picks: Pick[]): Set<string> {
  const byId = nodes(orgs);
  return new Set(picks.flatMap(p => (p.below ? branch(byId, p.id) : [p.id])));
}

/** The organizations a picked branch includes (not the branch's own top). */
export function covered<T extends TreeOrg>(orgs: T[], picks: Pick[]): Set<string> {
  const byId = nodes(orgs);
  return new Set(picks.filter(p => p.below).flatMap(p => branch(byId, p.id).slice(1)));
}

/** A click on a name: that organization alone. */
export function only(id: string): Pick[] {
  return [{ id, below: false }];
}

/** A box: in the scope or out of it (alone, as a box chooses). */
export function toggle(picks: Pick[], id: string): Pick[] {
  return picks.some(p => p.id === id) ? picks.filter(p => p.id !== id) : [...picks, { id, below: false }];
}

/** "+ all under it": its whole branch, or back to it alone; picking it if it was not. Picks
    inside the branch become redundant and go. */
export function toggleBelow<T extends TreeOrg>(orgs: T[], picks: Pick[], id: string): Pick[] {
  const mine = picks.find(p => p.id === id);
  if (mine?.below) return picks.map(p => (p.id === id ? { id, below: false } : p));
  const inside = new Set(branch(nodes(orgs), id));
  return [...picks.filter(p => !inside.has(p.id)), { id, below: true }];
}

/** Where a manager opens: their own organization and everything under it -
    else each top of what they manage, with everything under it. */
export function defaultPicks<T extends TreeOrg>(orgs: T[], ownId: string | null | undefined): Pick[] {
  if (ownId && orgs.some(o => o.tenant_id === ownId)) return [{ id: ownId, below: true }];
  return buildTree(orgs).map(n => ({ id: n.org.tenant_id, below: true }));
}

/** The request: `orgs` alone, `branches` with all under them. */
export function scopeParams(picks: Pick[]): { orgs: string[]; branches: string[] } {
  return { orgs: picks.filter(p => !p.below).map(p => p.id), branches: picks.filter(p => p.below).map(p => p.id) };
}

/** One pick as words are made of it: its name and, for a branch, how many under it. */
export interface PickWords { id: string; name: string; below: boolean; count: number }

/** The picks in the tree's order (the order the header names them in). */
export function pickWords<T extends TreeOrg>(orgs: T[], picks: Pick[]): PickWords[] {
  const byId = nodes(orgs);
  const order = [...byId.keys()];
  return picks
    .filter(p => byId.has(p.id))
    .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    .map(p => ({ id: p.id, name: byId.get(p.id)!.org.name, below: p.below,
                 count: p.below ? byId.get(p.id)!.ids.length - 1 : 0 }));
}

type Translate = (key: string, options?: Record<string, unknown>) => string;
/** A name kept in its own direction inside a sentence (FSI … PDI, as utils/bidi.ts). */
const isolate = (text: string) => `⁨${text}⁩`;

/** How many picks a sentence names before "and N more". */
const NAMED_PICKS = 3;

/** The scope in words, as the header and the filter say it: "Engineering
    Department and the 6 organizations under it and Brij demo" — the
    language's own list ("A, B and C" / "A, B ו-C"). */
export function scopeWords(words: PickWords[], t: Translate): string {
  const piece = (w: PickWords) => (w.below && w.count > 0
    ? t('connectorLibrary.scope.branch', { name: isolate(w.name), count: w.count })
    : isolate(w.name));
  const named = words.slice(0, NAMED_PICKS).map(piece);
  if (words.length > NAMED_PICKS) named.push(t('connectorLibrary.scope.more', { count: words.length - NAMED_PICKS }));
  if (named.length < 2) return named.join('');
  return t('connectorLibrary.scope.list', { rest: named.slice(0, -1).join(t('connectorLibrary.scope.listSep')),
                                            last: named[named.length - 1] });
}
